import React, { useEffect, useState } from 'react'
import { supabase } from '../../lib/supabase'

type QrPoint = {
  id: string
  code: string
  label: string | null
  area_type: string | null
  property_id: string
}

type Req = {
  id?: string
  contract_line_item_id: string
  qr_point_id: string
  enforcement: 'optional' | 'reminder' | 'required'
}

interface ContractLineItemQrRequirementsProps {
  contractId: string
  lineItemId: string
  propertyIds: string[]
}

export function ContractLineItemQrRequirements({
  contractId,
  lineItemId,
  propertyIds,
}: ContractLineItemQrRequirementsProps) {
  const [points, setPoints] = useState<QrPoint[]>([])
  const [reqs, setReqs] = useState<Req[]>([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = async () => {
    setError(null)
    try {
      if (propertyIds.length) {
        const { data, error: pErr } = await supabase
          .from('qr_points')
          .select('id, code, label, area_type, property_id')
          .in('property_id', propertyIds)
          .eq('status', 'active')
          .order('label')
        if (pErr) throw pErr
        setPoints(data || [])
      } else {
        setPoints([])
      }
      const { data: r, error: rErr } = await supabase
        .from('contract_line_item_qr_requirements')
        .select('id, contract_line_item_id, qr_point_id, enforcement')
        .eq('contract_line_item_id', lineItemId)
      if (rErr) throw rErr
      setReqs(r || [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load QR requirements')
    }
  }

  useEffect(() => {
    if (lineItemId) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItemId, propertyIds.join(',')])

  const upsert = async (pointId: string, enforcement: Req['enforcement'] | 'off') => {
    setSaving(true)
    setError(null)
    try {
      const existing = reqs.find((r) => r.qr_point_id === pointId)
      if (enforcement === 'off') {
        if (existing?.id) {
          const { error: err } = await supabase
            .from('contract_line_item_qr_requirements')
            .delete()
            .eq('id', existing.id)
          if (err) throw err
        }
      } else if (existing?.id) {
        const { error: err } = await supabase
          .from('contract_line_item_qr_requirements')
          .update({ enforcement })
          .eq('id', existing.id)
        if (err) throw err
      } else {
        const { data: auth } = await supabase.auth.getUser()
        const { error: err } = await supabase.from('contract_line_item_qr_requirements').insert({
          contract_id: contractId,
          contract_line_item_id: lineItemId,
          qr_point_id: pointId,
          enforcement,
          created_by: auth.user?.id || null,
        })
        if (err) throw err
      }
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  if (!propertyIds.length) {
    return <p className="text-xs text-gray-500">Link properties to this contract to set QR requirements.</p>
  }

  return (
    <div className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50">
      <p className="text-xs font-semibold text-gray-700 mb-2">QR scan requirements</p>
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      {points.length === 0 ? (
        <p className="text-xs text-gray-500">No active QR points on contract properties.</p>
      ) : (
        <ul className="space-y-2">
          {points.map((p) => {
            const current = reqs.find((r) => r.qr_point_id === p.id)?.enforcement || 'off'
            return (
              <li key={p.id} className="flex flex-wrap items-center gap-2 text-sm">
                <span className="flex-1 min-w-[140px] text-gray-800">
                  {p.label || p.code}
                  {p.area_type ? ` (${p.area_type})` : ''}
                </span>
                <select
                  disabled={saving}
                  value={current}
                  onChange={(e) => upsert(p.id, e.target.value as Req['enforcement'] | 'off')}
                  className="px-2 py-1 border border-gray-300 rounded text-xs"
                >
                  <option value="off">Off</option>
                  <option value="optional">Optional</option>
                  <option value="reminder">Reminder</option>
                  <option value="required">Required</option>
                </select>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

export default ContractLineItemQrRequirements
